import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, ShieldCheck, ShieldAlert, Sparkles, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";

const RESERVE_TONE = {
  healthy: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  tight: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  underfunded: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};
const money0 = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function FinancialForecastPage() {
  const { propertyId } = useProperty();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/admin/ai/finance/forecast", {
        params: { property_id: propertyId || "all" },
      });
      setResult(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  }, [propertyId]);

  // Auto-fetch on mount + property change (Claude call is ~5s so this is fine)
  useEffect(() => { load(); }, [load]);

  const source = result?._source || {};
  const monthlyHistory = source.monthly_history || [];
  const chart = [
    ...monthlyHistory.map((m) => ({ month: m.month, hist_net: m.net, projection: null })),
    ...(result?.monthly_projection || []).map((m) => ({ month: m.month, hist_net: null, projection: (m.income_est || 0) - (m.expense_est || 0) })),
  ];
  const categoryBars = Object.entries(source.category_totals || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([category, amount]) => ({ category, amount }));

  const RIcon = result?.reserve_health?.verdict === "healthy" ? ShieldCheck : ShieldAlert;

  return (
    <div>
      <PageHeader
        eyebrow="AI Features"
        title="Financial Forecast"
        subtitle="Claude projects the next 12 months of expenses, income, and reserve health from your ledger history."
        actions={
          <Button onClick={load} disabled={busy} variant="outline" className="border-white/10" data-testid="forecast-refresh">
            <RefreshCw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} /> {busy ? "Analyzing…" : "Recompute"}
          </Button>
        }
      />

      {busy && !result && (
        <Card className="border-white/10 bg-card p-16 flex items-center justify-center" data-testid="forecast-loading">
          <div className="text-sm text-zinc-400 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Claude is projecting the next 12 months…
          </div>
        </Card>
      )}

      {result && (
        <div className="space-y-4" data-testid="forecast-result">
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-white/10 bg-card p-5" data-testid="forecast-kpi-income">
              <div className="overline flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Projected income (12mo)</div>
              <div className="text-2xl font-semibold font-display mt-1 text-emerald-300 tabular-nums">{money0(result.next_12mo?.projected_income)}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Historical 12mo: {money0(result.historical_summary?.total_income)}</div>
            </Card>
            <Card className="border-white/10 bg-card p-5" data-testid="forecast-kpi-expense">
              <div className="overline">Projected expense (12mo)</div>
              <div className="text-2xl font-semibold font-display mt-1 text-rose-300 tabular-nums">{money0(result.next_12mo?.projected_expense)}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Historical 12mo: {money0(result.historical_summary?.total_expense)}</div>
            </Card>
            <Card className="border-white/10 bg-card p-5" data-testid="forecast-kpi-net">
              <div className="overline">Projected net</div>
              <div className={"text-2xl font-semibold font-display mt-1 tabular-nums " + ((result.next_12mo?.projected_net || 0) >= 0 ? "text-emerald-300" : "text-rose-300")}>{money0(result.next_12mo?.projected_net)}</div>
              <div className="text-[10px] text-zinc-500 mt-1">Confidence: <Badge variant="outline" className="border-white/10 text-[10px] ml-1">{result.next_12mo?.confidence || "medium"}</Badge></div>
            </Card>
          </div>

          {/* Reserve health */}
          {result.reserve_health && (
            <Card className={"p-5 border " + (RESERVE_TONE[result.reserve_health.verdict] || "border-white/10")} data-testid="forecast-reserve">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <RIcon className="h-6 w-6 mt-1" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-80">Reserve health</div>
                    <div className="text-lg font-semibold font-display mt-0.5">{result.reserve_health.verdict}</div>
                    <div className="text-xs mt-1 opacity-90">
                      Current {money0(result.reserve_health.current_est)} → 12mo {money0(result.reserve_health.next_12mo_est)} · {result.reserve_health.months_of_operating_reserve}mo operating buffer
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Chart: historical + projection */}
          {chart.length > 0 && (
            <Card className="border-white/10 bg-card p-5" data-testid="forecast-chart">
              <div className="overline mb-3">Monthly net · historical vs projected</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart}>
                    <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
                    <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => money0(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="hist_net" name="Historical net" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="projection" name="Projected net" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Category share */}
          {categoryBars.length > 0 && (
            <Card className="border-white/10 bg-card p-5" data-testid="forecast-categories">
              <div className="overline mb-3">Top expense drivers</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryBars} layout="vertical">
                    <XAxis type="number" stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="category" stroke="#71717a" fontSize={11} width={110} />
                    <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} formatter={(v) => money0(v)} />
                    <Bar dataKey="amount" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Anomalies + recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {result.anomalies?.length > 0 && (
              <Card className="border-amber-500/20 bg-amber-500/[0.03] p-5" data-testid="forecast-anomalies">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Anomalies detected
                </div>
                <div className="space-y-2">
                  {result.anomalies.map((a, i) => (
                    <div key={i} className="text-xs text-zinc-300" data-testid={`forecast-anomaly-${i}`}>
                      <b>{a.month} · {a.category}</b> — {a.note}
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {result.recommendations?.length > 0 && (
              <Card className="border-indigo-500/20 bg-indigo-500/[0.03] p-5" data-testid="forecast-recs">
                <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Recommendations
                </div>
                <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                  {result.recommendations.map((r, i) => <li key={i} data-testid={`forecast-rec-${i}`}>{r}</li>)}
                </ul>
              </Card>
            )}
          </div>

          {result.disclaimer && (
            <div className="text-[10px] text-zinc-500 pt-2 flex items-start gap-1" data-testid="forecast-disclaimer">
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
              <div>{result.disclaimer}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
